import { useEffect, useState, type ReactNode } from 'react';
import { Button } from './ui/Button';
import { Panel } from './ui/Panel';
import { Skeleton } from './ui/Skeleton';

/**
 * Generic paginated card grid for the Fleets page's three log sections
 * (Flight Logs, Battle Reports, Espionage Reports) — same responsive grid
 * (dense cards, up to 9 columns) and Prev/Next paging, differing only in
 * data source and card content. `fetchPage`'s cursor is opaque (a Firestore
 * `QueryDocumentSnapshot` for the real backend, a plain array index for
 * mock mode) — this component only round-trips it, never inspects it.
 */
export interface PaginatedLogSectionProps<T, C> {
    title: string;
    emptyMessage: string;
    /** Bump to force a reset back to page 1 (e.g. a fleet just resolved). */
    refreshKey: number;
    fetchPage: (cursor: C | null) => Promise<{ items: T[]; nextCursor: C | null }>;
    renderCard: (item: T) => ReactNode;
    keyOf: (item: T) => string;
}

const SKELETON_CARDS = 6;

export default function PaginatedLogSection<T, C>({
    title,
    emptyMessage,
    refreshKey,
    fetchPage,
    renderCard,
    keyOf,
}: PaginatedLogSectionProps<T, C>) {
    const [items, setItems] = useState<T[]>([]);
    const [cursorStack, setCursorStack] = useState<(C | null)[]>([null]);
    const [pageIndex, setPageIndex] = useState(0);
    const [nextCursor, setNextCursor] = useState<C | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function load(cursor: C | null, index: number) {
        setIsLoading(true);
        setError(null);
        try {
            const page = await fetchPage(cursor);
            setItems(page.items);
            setNextCursor(page.nextCursor);
            setPageIndex(index);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load');
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        setCursorStack([null]);
        load(null, 0).catch(() => { });
        // Only the refresh signal (and identity of the fetcher) should
        // re-trigger page-1 loading — paging itself is driven by the
        // Prev/Next handlers below, not this effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    function handleNext() {
        if (nextCursor === null || isLoading) return;
        setCursorStack((s) => [...s, nextCursor]);
        load(nextCursor, pageIndex + 1).catch(() => { });
    }

    function handlePrev() {
        if (pageIndex === 0 || isLoading) return;
        const prevCursor = cursorStack[pageIndex - 1] ?? null;
        setCursorStack((s) => s.slice(0, pageIndex));
        load(prevCursor, pageIndex - 1).catch(() => { });
    }

    return (
        <Panel variant="solid" className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
                <h2 className="text-title text-content-primary font-semibold">{title}</h2>
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={handlePrev}
                        disabled={pageIndex === 0 || isLoading}
                    >
                        ← Prev
                    </Button>
                    <span className="text-content-muted text-label">Page {pageIndex + 1}</span>
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={handleNext}
                        disabled={nextCursor === null || isLoading}
                    >
                        Next →
                    </Button>
                </div>
            </div>
            {error && <p className="text-status-danger text-body">{error}</p>}
            {isLoading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-2">
                    {Array.from({ length: SKELETON_CARDS }, (_, i) => (
                        <Skeleton key={i} variant="block" className="h-16" />
                    ))}
                </div>
            ) : !isLoading && items.length === 0 ? (
                <p className="text-content-muted text-body">{emptyMessage}</p>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-2">
                    {items.map((item) => (
                        <div key={keyOf(item)}>{renderCard(item)}</div>
                    ))}
                </div>
            )}
        </Panel>
    );
}
